'use client';

import { useEffect, useState, useMemo } from "react";
import { useEvento } from "@/store/evento/evento";
import { useEventoContext } from "../contexts/eventos";

export const CountdownBanner = () => {
    const { state: funcion } = useEvento.use.funcion();
    const { evento } = useEventoContext();
    const [timeLeft, setTimeLeft] = useState<{ d: number, textString: string, h: number, m: number, s: number } | null>(null);

    const targetDate = useMemo(() => {
        const activeFuncion = funcion || evento?.funciones?.[0];
        if (!activeFuncion?.fecha || !activeFuncion?.hora) return null;

        const [year, month, day] = activeFuncion.fecha.split('-').map(Number);
        const [hour, min] = activeFuncion.hora.split(':').map(Number);

        // Month en Date() es 0-indexed
        return new Date(year, month - 1, day, hour, min, 0).getTime();
    }, [funcion, evento]);

    useEffect(() => {
        if (!targetDate) {
            setTimeLeft(null);
            return;
        }

        const calculateTimeLeft = () => {
            const difference = targetDate - new Date().getTime();

            if (difference > 0) {
                setTimeLeft({
                    d: Math.floor(difference / (1000 * 60 * 60 * 24)),
                    textString: "Días",
                    h: Math.floor((difference / (1000 * 60 * 60)) % 24),
                    m: Math.floor((difference / 1000 / 60) % 60),
                    s: Math.floor((difference / 1000) % 60)
                });
            } else {
                setTimeLeft(null); // Show has already started/passed
            }
        };

        // Instantly calculate and then loop
        calculateTimeLeft();
        const timer = setInterval(calculateTimeLeft, 1000);

        return () => clearInterval(timer);
    }, [targetDate]);

    if (!timeLeft) return null;

    return (
        <div className="bg-gradient-to-r from-brand-red to-brand-red-dark text-white rounded-lg p-4 shadow-lg  overflow-hidden">
            <div className="flex flex-col items-center justify-center gap-2">
                {/* Faltan + ícono */}
                <div className="flex items-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    <span className="font-bold text-base sm:text-lg">Faltan</span>
                </div>

                {/* Números en columna */}
                <div className="flex flex-col items-center justify-center gap-1 sm:gap-2 w-full">
                    <div className="flex gap-2 sm:gap-3 items-center justify-center">
                        <span className="font-bold text-base sm:text-xl min-w-[1.5rem] sm:min-w-[2rem] text-right">{timeLeft.d}</span>
                        <span className="text-[9px] sm:text-xs uppercase tracking-wide min-w-[1.75rem] sm:min-w-[2.5rem]">Días</span>
                    </div>
                    <div className="flex gap-2 sm:gap-3 items-center justify-center">
                        <span className="font-bold text-base sm:text-xl min-w-[1.5rem] sm:min-w-[2rem] text-right">{timeLeft.h.toString().padStart(2, '0')}</span>
                        <span className="text-[9px] sm:text-xs uppercase tracking-wide min-w-[1.75rem] sm:min-w-[2.5rem]">Horas</span>
                    </div>
                    <div className="flex gap-2 sm:gap-3 items-center justify-center">
                        <span className="font-bold text-base sm:text-xl min-w-[1.5rem] sm:min-w-[2rem] text-right">{timeLeft.m.toString().padStart(2, '0')}</span>
                        <span className="text-[9px] sm:text-xs uppercase tracking-wide min-w-[1.75rem] sm:min-w-[2.5rem]">Min</span>
                    </div>
                    <div className="flex gap-2 sm:gap-3 items-center justify-center">
                        <span className="font-bold text-base sm:text-xl min-w-[1.5rem] sm:min-w-[2rem] text-right">{timeLeft.s.toString().padStart(2, '0')}</span>
                        <span className="text-[9px] sm:text-xs uppercase tracking-wide min-w-[1.75rem] sm:min-w-[2.5rem]">Seg</span>
                    </div>
                </div>
            </div>
        </div>
    );
};
